<template>
    <div
        class="redesign-extension-card-header"
        :style="headerStyle"
    >
        <!-- Top Row: Status Icon, Name, Summary Toggle -->
        <div class="flex justify-between mb-2">
            <div
                class="flex items-center"
                :class="{'w-4/6': cardsCols > 4}"
            >
                <!-- Status Icon with Tooltip -->
                <RedesignTooltip
                    placement="top"
                    effect="customized"
                    popper-class="custom"
                    :visible-arrow="false"
                >
                    <template #tooltip-content>
                        <div>{{ statusText }}</div>
                    </template>
                    <div
                        :style="{ backgroundColor: statusColor }"
                        class="p-1 redesign-extension-card-header__icon"
                    >
                        <RedesignBaseIcon
                            :name="statusIcon"
                            :fill="statusColor"
                            :size="fontSize"
                            color="white"
                        />
                    </div>
                </RedesignTooltip>

                <!-- User Name -->
                <div
                    class="text-nowrap mx-2 font-bold text-steel redesign-extension-card-header__title"
                    :style="nameStyling"
                >
                    {{ userName }}
                </div>
            </div>

            <!-- Summary Toggle Button -->
            <div>
                <RedesignToggleButton
                    v-if="showStatistics"
                    bg-color="blue"
                    border-radius="18px"
                    size="xs"
                    :on-label="$t('widget.summary')"
                    :off-label="$t('widget.summary')"
                    type="primary"
                    :round="true"
                    :disabled="false"
                    @toggle="emit('toggle-summary', $event)"
                />
            </div>
        </div>

        <!-- Bottom Row: Timer, Statistics Tags -->
        <div
            class="flex justify-between redesign-extension-card-header__content"
            :class="{ 'redesign-extension-card-header__content--hidden': isContentHidden }"
        >
            <!-- Timer Display -->
            <div
                :style="{ color: timerColor }"
                class="uppercase text-3xl font-bold gap-1 flex items-center"
                :class="{ 'gap-2': !isSmallCard }"
            >
                <div
                    style="font-variant-numeric: tabular-nums;"
                    :style="timerStyling"
                >
                    {{ timerDisplayTime }}
                </div>
                <div
                    v-show="isAlertActive && !isSmallCard"
                    class="p-0-5 rounded-full"
                    :style="{ background: alertIconBgColor }"
                >
                    <RedesignBaseIcon
                        :fill="alertIconBgColor"
                        name="IconCircleAlert"
                        :size="30"
                    />
                </div>
                <RedesignWrapupCountdown
                    :extension="extension"
                    :text-size="wrapupTextSize"
                />
            </div>

            <!-- Statistics Tags -->
            <div
                v-if="showStatistics && statistics.length"
                class="flex gap-0.5 items-center"
            >
                <div
                    v-for="(tag, index) in statistics"
                    :key="`stat-${index}`"
                >
                    <RedesignTooltip>
                        <template #tooltip-content>
                            <ExtensionCardStatisticTooltip :items="tag.fullSummary" />
                        </template>
                        <RedesignTag
                            v-show="tag.showAlways"
                            :color="tag.color"
                            :tag-content="tag.tagContent"
                            :border-color="tag.borderColor"
                            :padding-classes="'py-1.5 px-2'"
                            outline
                            class="cursor-pointer text-sm"
                        />
                    </RedesignTooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignTag from '@/components/RedesignBaseComponents/RedesignTag.vue'
import RedesignToggleButton from '@/components/RedesignBaseComponents/RedesignToggleButton.vue'
import RedesignTooltip from '@/components/RedesignBaseComponents/RedesignTooltip.vue'
import ExtensionCardStatisticTooltip from '@/components/RedesignComponents/Cards/ExtensionCard/ExtensionCardStatisticTooltip.vue'
import RedesignWrapupCountdown from '@/components/RedesignComponents/RedesignTableComponents/RedesignWrapupCountdown.vue'
import { HeaderStatisticTag } from '@/composables/useExtensionCardStatistics'
import type { Extension } from '@/types/extension'

const props = withDefaults(
    defineProps<{
        // Display props
        userName: string
        statusText: string
        statusIcon: string
        statusColor: string
        fontSize: number
        cardsCols: number
        showStatistics: boolean
        isContentHidden?: boolean
        // Timer props
        timerDisplayTime: string
        timerColor: string
        timerFontSize: number
        // Alert props
        isAlertActive: boolean
        alertIconBgColor: string
        // Statistics
        statistics: HeaderStatisticTag[]
        // Extension (for wrapup countdown)
        extension?: Extension
    }>(),
    {
        isContentHidden: false
    }
)

const emit = defineEmits<{
    (e: 'toggle-summary', value: boolean): void
}>()

// Computed styles
const isSmallCard = computed(() => props.cardsCols > 4)

const nameStyling = computed(() => ({
    fontSize: `${props.fontSize}px`,
    lineHeight: `${props.fontSize}px`
}))

const timerStyling = computed(() => ({
    fontSize: `${props.timerFontSize}px`,
    lineHeight: `${props.timerFontSize}px`
}))

const wrapupTextSize = computed(() => {
    const size = isSmallCard.value ? Math.round(props.timerFontSize * 0.5) : Math.round(props.timerFontSize * 0.6)
    return `${size}px`
})

const headerStyle = computed(() => ({
    '--extension-border-color': props.statusColor
}))
</script>

<style lang="scss">
.redesign-extension-card-header {
    @apply px-3 pt-3 pb-2;
    border-bottom: 1px solid var(--extension-border-color);

    &__icon {
        border-radius: 4px;
    }

    &__title {
        color: var(--mainTextColor-color);
    }

    .text-nowrap {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    &__content {
        overflow: hidden;
        transition: max-height 0.5s ease-out, opacity 0.5s ease-out;
        max-height: 80px;
        opacity: 1;
        will-change: max-height, opacity;

        &--hidden {
            max-height: 0;
            opacity: 0;
        }
    }

}
</style>
